import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { getMotivosRechazo, postAltaRechazo } from "./ApiServices";
import { formatMoneda } from "../pago_proveedores/utils";
import {
  ORIGEN_RECHAZO,
  labelOrigenRechazo,
} from "./chequesRechazos.constants";
import { invalidateListaNegraCache } from "../cheques_lista_negra/ApiServices";

const formInicial = () => ({
  origen: ORIGEN_RECHAZO.RECLAMO_PROVEEDOR,
  es_cheque_propio: "",
  importe_cheque: "",
  gastos_banco: "",
  gastos_trasladados: "",
  obs: "",
  id_proveedor: "",
  id_cliente: "",
  codigo: "",
});

const numOpcional = (value) => {
  const t = String(value || "").trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : undefined;
};

const idOpcional = (value) => {
  const t = String(value || "").trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

export default function ModalAltaRechazo({
  open,
  onClose,
  cheque,
  onOk,
  postRechazo,
}) {
  const esFisico = Boolean(postRechazo);
  const [form, setForm] = useState(formInicial);
  const [motivos, setMotivos] = useState([]);
  const [cargandoMotivos, setCargandoMotivos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;

    const propio =
      cheque?.es_cheque_propio === true || cheque?.es_cheque_propio === 1
        ? "si"
        : cheque?.es_cheque_propio === false || cheque?.es_cheque_propio === 0
          ? "no"
          : "";

    setForm({
      ...formInicial(),
      es_cheque_propio: propio,
      importe_cheque: cheque?.importe != null ? String(cheque.importe) : "",
      id_proveedor:
        cheque?.id_proveedor != null ? String(cheque.id_proveedor) : "",
      id_cliente: cheque?.id_cliente != null ? String(cheque.id_cliente) : "",
    });
    setError("");

    let cancelado = false;
    setCargandoMotivos(true);
    (async () => {
      try {
        const lista = await getMotivosRechazo();
        if (cancelado) return;
        setMotivos(Array.isArray(lista) ? lista : []);
        if (!lista?.length) {
          setError("No hay motivos de rechazo disponibles");
        }
      } catch (err) {
        if (cancelado) return;
        setMotivos([]);
        setError(err.message || "No se pudieron cargar los motivos de rechazo");
      } finally {
        if (!cancelado) setCargandoMotivos(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [open, cheque]);

  const setCampo = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const guardar = async () => {
    const idAlta = cheque?.id_instrumento_bancario ?? cheque?.id;
    if (!idAlta) return;

    const codigo = Number(form.codigo);
    const obs = form.obs.trim();

    if (!Number.isFinite(codigo) || codigo <= 0) {
      setError("Seleccioná el motivo de rechazo");
      return;
    }
    if (!obs) {
      setError("Las observaciones son obligatorias");
      return;
    }

    setGuardando(true);
    setError("");

    try {
      const body = {
        codigo,
        obs,
      };

      if (!esFisico) {
        body.origen = form.origen;
        if (form.es_cheque_propio === "si") body.es_cheque_propio = true;
        if (form.es_cheque_propio === "no") body.es_cheque_propio = false;

        const importe = numOpcional(form.importe_cheque);
        const gastosBanco = numOpcional(form.gastos_banco);
        const gastosTrasladados = numOpcional(form.gastos_trasladados);
        const idProveedor = idOpcional(form.id_proveedor);
        const idCliente = idOpcional(form.id_cliente);

        if (importe != null) body.importe_cheque = importe;
        if (gastosBanco != null) body.gastos_banco = gastosBanco;
        if (gastosTrasladados != null) body.gastos_trasladados = gastosTrasladados;
        if (idProveedor != null) body.id_proveedor = idProveedor;
        if (idCliente != null) body.id_cliente = idCliente;
      }

      const alta = postRechazo || postAltaRechazo;
      const data = await alta(idAlta, body);
      invalidateListaNegraCache();
      onOk?.(data, form.origen);
      onClose?.();
    } catch (err) {
      setError(err.message || "Error al registrar el rechazo");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={guardando ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        Registrar rechazo bancario — N° {cheque?.nro_cheque || cheque?.id || ""}
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Stack spacing={2.5}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <Typography variant="body2" color="text.secondary">
            
            {cheque?.importe != null ? formatMoneda(cheque.importe) : "—"}.
            El CUIT librador entrará en lista negra automáticamente.
          </Typography>
          <TextField
            select
            size="small"
            fullWidth
            required
            label="Motivo de rechazo"
            value={form.codigo}
            onChange={setCampo("codigo")}
            disabled={guardando || cargandoMotivos}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true }}
            helperText={
              cargandoMotivos
                ? "Cargando catálogo..."
                : "Obligatorio. Catálogo bancario."
            }
          >
            <MenuItem value="" disabled>
              {cargandoMotivos ? "Cargando..." : "Seleccioná un motivo"}
            </MenuItem>
            {motivos.map((m) => (
              <MenuItem key={m.codigo} value={String(m.codigo)}>
                {m.codigo} — {m.motivo}
              </MenuItem>
            ))}
          </TextField>
          {!esFisico ? (
            <>
              <TextField
                select
                size="small"
                fullWidth
                required
                label="Origen del rechazo"
                value={form.origen}
                onChange={setCampo("origen")}
                disabled={guardando}
              >
                <MenuItem value={ORIGEN_RECHAZO.RECLAMO_PROVEEDOR}>
                  {labelOrigenRechazo(ORIGEN_RECHAZO.RECLAMO_PROVEEDOR)} — va a
                  Finanzas (ND)
                </MenuItem>
                <MenuItem value={ORIGEN_RECHAZO.EXTRACTO_BANCARIO}>
                  {labelOrigenRechazo(ORIGEN_RECHAZO.EXTRACTO_BANCARIO)} — va a
                  Pagos
                </MenuItem>
              </TextField>
              <TextField
                select
                size="small"
                fullWidth
                label="¿Cheque propio?"
                value={form.es_cheque_propio}
                onChange={setCampo("es_cheque_propio")}
                disabled={guardando}
                helperText="Opcional. Si es no propio y pasaron 30 días desde fecha de pago, la reposición puede quedar exenta."
              >
                <MenuItem value="">Sin indicar (lo calcula el back)</MenuItem>
                <MenuItem value="si">Sí — propio</MenuItem>
                <MenuItem value="no">No — no propio</MenuItem>
              </TextField>
              <TextField
                size="small"
                fullWidth
                label="Importe cheque (opcional)"
                value={form.importe_cheque}
                onChange={setCampo("importe_cheque")}
                disabled={guardando}
              />
              <TextField
                size="small"
                fullWidth
                label="Gastos banco (opcional)"
                value={form.gastos_banco}
                onChange={setCampo("gastos_banco")}
                disabled={guardando}
              />
              <TextField
                size="small"
                fullWidth
                label="Gastos trasladados (opcional)"
                value={form.gastos_trasladados}
                onChange={setCampo("gastos_trasladados")}
                disabled={guardando}
              />
              <TextField
                size="small"
                fullWidth
                label="ID proveedor (opcional)"
                value={form.id_proveedor}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    id_proveedor: e.target.value.replace(/\D/g, ""),
                  }))
                }
                disabled={guardando}
              />
              <TextField
                size="small"
                fullWidth
                label="ID cliente (opcional)"
                value={form.id_cliente}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    id_cliente: e.target.value.replace(/\D/g, ""),
                  }))
                }
                disabled={guardando}
              />
            </>
          ) : null}
          <TextField
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
            label="Observaciones"
            value={form.obs}
            onChange={setCampo("obs")}
            disabled={guardando}
            helperText="Obligatorio."
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={guardando}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={guardar}
          disabled={guardando || cargandoMotivos || !form.codigo}
          startIcon={
            guardando ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Registrar rechazo bancario
        </Button>
      </DialogActions>
    </Dialog>
  );
}
